import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Filter, PackageOpen } from "lucide-react";
import BuyModal from "@/components/BuyModal";

const CATS = [
    { id: "all", label: "All" },
    { id: "racquet", label: "Racquets" },
    { id: "balls", label: "Balls" },
    { id: "apparel", label: "Apparel" },
    { id: "accessories", label: "Accessories" },
];

export default function FrameworkStore() {
    const [products, setProducts] = useState([]);
    const [cat, setCat] = useState("all");
    const [buying, setBuying] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);

    useEffect(() => {
        let active = true;

        const loadProducts = async () => {
            setLoading(true);
            setLoadError(false);

            try {
                const endpoint =
                    cat === "all" ? "/products" : `/products?category=${cat}`;
                const response = await api.get(endpoint);

                if (active) {
                    setProducts(Array.isArray(response.data) ? response.data : []);
                }
            } catch (error) {
                console.error("Unable to load Framework products:", error);

                if (active) {
                    setProducts([]);
                    setLoadError(true);
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        loadProducts();

        return () => {
            active = false;
        };
    }, [cat]);

    return (
        <div data-testid="framework-store-page">
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-12">
                <div className="fw-label mb-4">// Framework Pro Shop</div>
                <h1 className="fw-display text-5xl sm:text-7xl">
                    Store. <span className="text-[#a6ce39]">Restrung.</span>
                </h1>
                <p className="mt-6 text-white/60 max-w-2xl text-lg">
                    Refurbished frames, apparel, and match essentials selected for
                    real players who actually hit the ball.
                </p>
            </section>

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-6 flex flex-wrap items-center gap-3">
                <Filter size={16} className="text-white/50" />

                {CATS.map((category) => (
                    <button
                        key={category.id}
                        type="button"
                        onClick={() => setCat(category.id)}
                        data-testid={`fw-store-filter-${category.id}`}
                        className={`fw-mono text-[10px] uppercase tracking-[0.25em] px-4 py-2 border transition-all ${
                            cat === category.id
                                ? "border-[#a6ce39] bg-[#a6ce39] text-black"
                                : "border-white/20 text-white/70 hover:border-white/50"
                        }`}
                    >
                        {category.label}
                    </button>
                ))}
            </section>

            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
                {loading ? (
                    <div
                        className="border border-white/10 bg-[#0f0f10] p-10 text-center"
                        data-testid="fw-store-loading"
                    >
                        <p className="fw-mono text-xs uppercase tracking-[0.22em] text-white/45">
                            Loading inventory…
                        </p>
                    </div>
                ) : loadError ? (
                    <div
                        className="border border-white/10 bg-[#0f0f10] p-10 text-center"
                        data-testid="fw-store-error"
                    >
                        <PackageOpen
                            size={34}
                            strokeWidth={1.5}
                            className="mx-auto text-[#a6ce39]"
                        />
                        <h2 className="fw-heading text-2xl mt-5">
                            The Pro Shop could not load.
                        </h2>
                        <p className="mt-3 text-white/55 max-w-xl mx-auto">
                            Please refresh the page or check back shortly.
                        </p>
                    </div>
                ) : products.length === 0 ? (
                    <div
                        className="border border-white/10 bg-[#0f0f10] p-10 sm:p-14 text-center"
                        data-testid="fw-store-empty"
                    >
                        <PackageOpen
                            size={38}
                            strokeWidth={1.5}
                            className="mx-auto text-[#a6ce39]"
                        />
                        <div className="fw-label mt-6">// Inventory Update</div>
                        <h2 className="fw-display text-3xl sm:text-4xl mt-3">
                            New products are being added.
                        </h2>
                        <p className="mt-4 text-white/55 max-w-2xl mx-auto leading-relaxed">
                            Framework Pro Shop inventory will appear here only after
                            it has been added through the admin portal.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {products.map((product) => (
                            <article
                                key={product.id}
                                className="fw-card group overflow-hidden flex flex-col"
                                data-testid={`fw-product-${product.id}`}
                            >
                                <div className="aspect-square overflow-hidden bg-[#0a0a0a]">
                                    {product.image ? (
                                        <img
                                            src={product.image}
                                            alt={product.name}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-white/20">
                                            <PackageOpen size={42} strokeWidth={1.3} />
                                        </div>
                                    )}
                                </div>

                                <div className="p-6 flex-1 flex flex-col">
                                    <div className="flex items-center justify-between gap-4 mb-3">
                                        <span className="fw-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                                            {product.category}
                                        </span>

                                        {product.condition && (
                                            <span className="fw-mono text-[10px] uppercase tracking-[0.25em] text-[#a6ce39] text-right">
                                                {product.condition}
                                            </span>
                                        )}
                                    </div>

                                    <h3 className="fw-heading text-lg leading-tight">
                                        {product.name}
                                    </h3>

                                    <p className="mt-2 text-white/50 text-sm flex-1">
                                        {product.description}
                                    </p>

                                    <div className="mt-5 flex items-center justify-between gap-4">
                                        <span className="fw-heading text-2xl text-[#a6ce39]">
                                            ${Number(product.price || 0).toFixed(2)}
                                        </span>

                                        <button
                                            type="button"
                                            className="fw-btn-primary text-xs px-4 py-2"
                                            onClick={() =>
                                                setBuying({
                                                    type: "product",
                                                    id: product.id,
                                                    name: product.name,
                                                    description: product.description,
                                                    price: product.price,
                                                })
                                            }
                                            data-testid={`fw-product-buy-${product.id}`}
                                        >
                                            Buy now
                                        </button>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </section>

            <BuyModal
                open={Boolean(buying)}
                onClose={() => setBuying(null)}
                item={buying}
                brand="framework"
            />
        </div>
    );
}
